import { type LandingListFAQ } from '@/types';
import AiToolsIcon from '@/public/icons/pricing/ai-tools.svg';
import GeneratorIcon from '@/public/icons/pricing/bg-remover.svg';
import CheckCircleIcon from '@/public/icons/pricing/check-circle.svg';
import HighResIcon from '@/public/icons/pricing/high-res.svg';
import InfinityIcon from '@/public/icons/pricing/infinity.svg';
import StorageIcon from '@/public/icons/pricing/storage.svg';
import TemplatesIcon from '@/public/icons/pricing/templates.svg';
import TopModelsIcon from '@/public/icons/pricing/top-models.svg';

export type PricingFeatureColor =
	| 'teal'
	| 'purple'
	| 'pink'
	| 'violet'
	| 'yellow'
	| 'indigo';

export interface PricingFeature {
	text: string;
	color: PricingFeatureColor;
	Icon: React.ElementType<React.SVGProps<SVGSVGElement>>;
}

export const PRICING_FEATURES: PricingFeature[] = [
	{ text: 'features_item1', color: 'teal', Icon: GeneratorIcon },
	{ text: 'features_item2', color: 'purple', Icon: AiToolsIcon },
	{ text: 'features_item3', color: 'pink', Icon: TopModelsIcon },
	{ text: 'features_item4', color: 'violet', Icon: TemplatesIcon },
	{ text: 'features_item5', color: 'yellow', Icon: InfinityIcon },
	{ text: 'features_item6', color: 'teal', Icon: HighResIcon },
	{ text: 'features_item7', color: 'yellow', Icon: StorageIcon },
	{ text: 'features_item8', color: 'indigo', Icon: CheckCircleIcon },
];

export interface PricingPlanBilling {
	/** Charged today */
	priceToday: string;
	/** Charged on every renewal */
	renewPrice: string;
	/** Translation key of the billing period word */
	periodKey: string;
	/** Set on intro-offer plans: translation key of the trial length */
	trialDurationKey?: string;
}

export interface PricingPlan {
	/** Local id for the radio selection */
	id: string;
	/** Product name from the web app's PAYWALL_PLANS, for the checkout hand-off */
	productName?: string;
	label: string;
	price: string;
	/** Crossed-out price next to the current one */
	priceOld?: string;
	/** Shown after the price, e.g. "/month" */
	suffix?: string;
	badge?: string;
	/** Fills `{percent}` in the badge copy — set on badges computed from the prices */
	badgePercent?: string;
	badgeVariant?: 'primary' | 'save';
	/** Feeds the legal copy under the plans */
	billing: PricingPlanBilling;
	/** Two-line billing note under the label (default plan set) */
	note?: {
		/** Charged today */
		price: string;
		/** Translation key of the renewal line */
		renewKey: string;
		/** Renewal price */
		renewPrice: string;
	};
}

/** Shown to visitors in the `non_tier_1` monetization test (see getMonetizationTest) */
export const PRICING_PLANS_NON_TIER_1: PricingPlan[] = [
	{
		id: 'trial-limited',
		productName: 'getlift_1month_49.99USD_1week_0.99USD_trial',
		label: 'plans_trialLimited',
		price: '$0.99',
		badge: 'plans_badgeBestForFirstTimers',
		badgeVariant: 'primary',
		billing: {
			priceToday: '$0.99',
			renewPrice: '$49.99',
			periodKey: 'plansLegal_periodMonth',
			trialDurationKey: 'plansLegal_trial7Day',
		},
	},
	{
		id: 'quarterly',
		productName: 'liftapp.ai_3month_multi_currency_89.99USD_no_trial',
		label: 'plans_quarterly',
		price: '$29.99',
		suffix: 'plans_perMonth',
		billing: {
			priceToday: '$89.99',
			renewPrice: '$89.99',
			periodKey: 'plansLegal_period3Months',
		},
	},
	{
		id: 'annual',
		productName: 'getlift_1year_199.99USD_no_trial',
		label: 'plans_annual',
		price: '$16.66',
		suffix: 'plans_perMonth',
		badge: 'plans_badgeSave',
		badgeVariant: 'save',
		billing: {
			priceToday: '$199.99',
			renewPrice: '$199.99',
			periodKey: 'plansLegal_periodYear',
		},
	},
];

export const PRICING_PLAN_NON_TIER_1_NO_TRIAL: PricingPlan = {
	id: 'monthly',
	productName: 'liftapp.ai_1month_multi_currency_49.99USD_no_trial',
	label: 'plans_monthly',
	price: '$49.99',
	badge: 'plans_badgeBestForFirstTimers',
	badgeVariant: 'primary',
	billing: {
		priceToday: '$49.99',
		renewPrice: '$49.99',
		periodKey: 'plansLegal_periodMonth',
	},
};

/** The recommended plan is preselected, same rule as the web app's paywall */
export const getDefaultPlanId = (plans: PricingPlan[]): string =>
	plans.find(plan => plan.badgeVariant === 'primary')?.id ?? plans[0].id;

export interface PricingTestimonial {
	id: string;
	date: string;
	author: string;
	text: string;
	link: string;
}

export const PRICING_TESTIMONIALS: PricingTestimonial[] = [
	{
		id: 'helene',
		date: '12.06.2026',
		author: 'Hélène',
		text: 'It was simple, inexpensive and I got to make all kinds of IA pictures from the most professionnal to the goofiest',
		link: 'https://www.trustpilot.com/reviews/6a2c747c9f14e9fc0955c10d',
	},
	{
		id: 'kia-roussel',
		date: '08.06.2026',
		author: 'Kia Roussel',
		text: 'I truly love this site-it’s so worth it!! You absolutely have to give it a try!!!!',
		link: 'https://www.trustpilot.com/reviews/6a26cd61364555aec8ae0c9f',
	},
	{
		id: 'janice-andrews',
		date: '04.06.2026',
		author: 'Janice Andrews',
		text: 'Quick results that gave me a great idea for a new hairstyle.',
		link: 'https://www.trustpilot.com/reviews/6a21aafdc0fffb813192b118',
	},
	{
		id: 'barbara-hart',
		date: '01.06.2026',
		author: 'barbara hart',
		text: 'It wasn`t too difficult to get the result I wanted. There were lots of options, and these allowed self-correction.',
		link: 'https://www.trustpilot.com/reviews/6a19dc042b8342003624fd1c',
	},
	{
		id: 'ken-platt',
		date: '29.05.2026',
		author: 'Ken Platt',
		text: 'Easy to and simple to use',
		link: 'https://www.trustpilot.com/reviews/6a19aac52ea0e0cfc6261f10',
	},
	{
		id: 'jennifer-gonzales',
		date: '29.05.2026',
		author: 'Jennifer Gonzales',
		text: 'Literal magic! I love it!',
		link: 'https://www.trustpilot.com/reviews/6a190f4d9ed764a4e83cfaf9',
	},
	{
		id: 'charlie-pouliot',
		date: '26.05.2026',
		author: 'Charlie Pouliot',
		text: 'Lift AI Photo Editor is amazing- perfectly and seamlessly did exactly what I needed!',
		link: 'https://www.trustpilot.com/reviews/6a15111696451331a4a9c59b',
	},
];

export const PRICING_FAQ: {
	title: string;
	buttonText: string;
	list: LandingListFAQ[];
} = {
	title: 'faqData_mainTitle',
	buttonText: 'faqData_buttonText',
	list: [
		{ title: 'faqData_title1', subtitle: 'faqData_subtitle1' },
		{ title: 'faqData_title2', subtitle: 'faqData_subtitle2' },
		{ title: 'faqData_title3', subtitle: 'faqData_subtitle3' },
		{ title: 'faqData_title4', subtitle: 'faqData_subtitle4' },
		{
			title: 'faqData_title5',
			subtitle: 'faqData_subtitle5',
			blocks: [
				{ text: 'faqData_subtitle5Heading1', heading: true },
				{ text: 'faqData_subtitle5Text1' },
				{ text: 'faqData_subtitle5Text2' },
				{ text: 'faqData_subtitle5Heading2', heading: true },
				{ text: 'faqData_subtitle5Text3' },
				{ text: 'faqData_subtitle5Text4' },
			],
		},
		{ title: 'faqData_title6', subtitle: 'faqData_subtitle6' },
	],
};
